/**
 * Box-drawing connectivity.
 *
 * Every box character is modelled as four edge weights (north, east, south,
 * west) where 0 = none, 1 = light, 2 = heavy, 3 = double. Drawing therefore
 * never "replaces a character": the tool accumulates the edges a cell needs and
 * looks up the glyph that has exactly those edges, which is what makes
 * intersections, T-junctions and corners fall out automatically — including the
 * mixed light/heavy and light/double glyphs Unicode provides.
 */

export type Weight = 0 | 1 | 2 | 3;
export type LineStyle = 'light' | 'heavy' | 'double' | 'rounded' | 'dashed';

export interface Edges {
  n: Weight;
  e: Weight;
  s: Weight;
  w: Weight;
}

export const NO_EDGES: Edges = { n: 0, e: 0, s: 0, w: 0 };

export function styleWeight(style: LineStyle): Weight {
  switch (style) {
    case 'heavy':
      return 2;
    case 'double':
      return 3;
    default:
      return 1;
  }
}

/** [char, n, e, s, w] */
type Entry = [string, Weight, Weight, Weight, Weight];

const TABLE: Entry[] = [
  ['─', 0, 1, 0, 1], ['━', 0, 2, 0, 2],
  ['│', 1, 0, 1, 0], ['┃', 2, 0, 2, 0],
  ['┌', 0, 1, 1, 0], ['┍', 0, 2, 1, 0], ['┎', 0, 1, 2, 0], ['┏', 0, 2, 2, 0],
  ['┐', 0, 0, 1, 1], ['┑', 0, 0, 1, 2], ['┒', 0, 0, 2, 1], ['┓', 0, 0, 2, 2],
  ['└', 1, 1, 0, 0], ['┕', 1, 2, 0, 0], ['┖', 2, 1, 0, 0], ['┗', 2, 2, 0, 0],
  ['┘', 1, 0, 0, 1], ['┙', 1, 0, 0, 2], ['┚', 2, 0, 0, 1], ['┛', 2, 0, 0, 2],
  ['├', 1, 1, 1, 0], ['┝', 1, 2, 1, 0], ['┞', 2, 1, 1, 0], ['┟', 1, 1, 2, 0],
  ['┠', 2, 1, 2, 0], ['┡', 2, 2, 1, 0], ['┢', 1, 2, 2, 0], ['┣', 2, 2, 2, 0],
  ['┤', 1, 0, 1, 1], ['┥', 1, 0, 1, 2], ['┦', 2, 0, 1, 1], ['┧', 1, 0, 2, 1],
  ['┨', 2, 0, 2, 1], ['┩', 2, 0, 1, 2], ['┪', 1, 0, 2, 2], ['┫', 2, 0, 2, 2],
  ['┬', 0, 1, 1, 1], ['┭', 0, 1, 1, 2], ['┮', 0, 2, 1, 1], ['┯', 0, 2, 1, 2],
  ['┰', 0, 1, 2, 1], ['┱', 0, 1, 2, 2], ['┲', 0, 2, 2, 1], ['┳', 0, 2, 2, 2],
  ['┴', 1, 1, 0, 1], ['┵', 1, 1, 0, 2], ['┶', 1, 2, 0, 1], ['┷', 1, 2, 0, 2],
  ['┸', 2, 1, 0, 1], ['┹', 2, 1, 0, 2], ['┺', 2, 2, 0, 1], ['┻', 2, 2, 0, 2],
  ['┼', 1, 1, 1, 1], ['┽', 1, 1, 1, 2], ['┾', 1, 2, 1, 1], ['┿', 1, 2, 1, 2],
  ['╀', 2, 1, 1, 1], ['╁', 1, 1, 2, 1], ['╂', 2, 1, 2, 1], ['╃', 2, 1, 1, 2],
  ['╄', 2, 2, 1, 1], ['╅', 1, 1, 2, 2], ['╆', 1, 2, 2, 1], ['╇', 2, 2, 1, 2],
  ['╈', 1, 2, 2, 2], ['╉', 2, 1, 2, 2], ['╊', 2, 2, 2, 1], ['╋', 2, 2, 2, 2],
  ['═', 0, 3, 0, 3], ['║', 3, 0, 3, 0],
  ['╒', 0, 3, 1, 0], ['╓', 0, 1, 3, 0], ['╔', 0, 3, 3, 0],
  ['╕', 0, 0, 1, 3], ['╖', 0, 0, 3, 1], ['╗', 0, 0, 3, 3],
  ['╘', 1, 3, 0, 0], ['╙', 3, 1, 0, 0], ['╚', 3, 3, 0, 0],
  ['╛', 1, 0, 0, 3], ['╜', 3, 0, 0, 1], ['╝', 3, 0, 0, 3],
  ['╞', 1, 3, 1, 0], ['╟', 3, 1, 3, 0], ['╠', 3, 3, 3, 0],
  ['╡', 1, 0, 1, 3], ['╢', 3, 0, 3, 1], ['╣', 3, 0, 3, 3],
  ['╤', 0, 3, 1, 3], ['╥', 0, 1, 3, 1], ['╦', 0, 3, 3, 3],
  ['╧', 1, 3, 0, 3], ['╨', 3, 1, 0, 1], ['╩', 3, 3, 0, 3],
  ['╪', 1, 3, 1, 3], ['╫', 3, 1, 3, 1], ['╬', 3, 3, 3, 3],
  ['╴', 0, 0, 0, 1], ['╵', 1, 0, 0, 0], ['╶', 0, 1, 0, 0], ['╷', 0, 0, 1, 0],
  ['╸', 0, 0, 0, 2], ['╹', 2, 0, 0, 0], ['╺', 0, 2, 0, 0], ['╻', 0, 0, 2, 0],
  ['╼', 0, 2, 0, 1], ['╽', 1, 0, 2, 0], ['╾', 0, 1, 0, 2], ['╿', 2, 0, 1, 0],
];

/** Rounded corners are light corners with a different glyph. */
const ROUNDED: Entry[] = [
  ['╭', 0, 1, 1, 0],
  ['╮', 0, 0, 1, 1],
  ['╯', 1, 0, 0, 1],
  ['╰', 1, 1, 0, 0],
];

/** Dashed glyphs read as light lines but are never generated automatically. */
const DASHED: Entry[] = [
  ['┄', 0, 1, 0, 1], ['┅', 0, 2, 0, 2],
  ['┆', 1, 0, 1, 0], ['┇', 2, 0, 2, 0],
  ['┈', 0, 1, 0, 1], ['┉', 0, 2, 0, 2],
  ['┊', 1, 0, 1, 0], ['┋', 2, 0, 2, 0],
  ['╌', 0, 1, 0, 1], ['╍', 0, 2, 0, 2],
  ['╎', 1, 0, 1, 0], ['╏', 2, 0, 2, 0],
];

function key(e: Edges): number {
  return e.n * 64 + e.e * 16 + e.s * 4 + e.w;
}

const EDGES_BY_CHAR = new Map<string, Edges>();
const CHAR_BY_EDGES = new Map<number, string>();
const ROUNDED_BY_EDGES = new Map<number, string>();

for (const [ch, n, e, s, w] of TABLE) {
  const edges: Edges = { n, e, s, w };
  EDGES_BY_CHAR.set(ch, edges);
  CHAR_BY_EDGES.set(key(edges), ch);
}
for (const [ch, n, e, s, w] of ROUNDED) {
  const edges: Edges = { n, e, s, w };
  EDGES_BY_CHAR.set(ch, edges);
  ROUNDED_BY_EDGES.set(key(edges), ch);
}
for (const [ch, n, e, s, w] of DASHED) {
  EDGES_BY_CHAR.set(ch, { n, e, s, w });
}

export function isBoxChar(ch: string): boolean {
  return EDGES_BY_CHAR.has(ch);
}

export function edgesOf(ch: string): Edges | null {
  const e = EDGES_BY_CHAR.get(ch);
  return e ? { ...e } : null;
}

/**
 * Glyph for an edge set.
 *
 * Unicode has no glyph for some mixed combinations (there is no double+heavy
 * junction, for instance), so unmatched sets fall back to the dominant weight
 * across all edges rather than dropping the cell.
 */
export function charForEdges(edges: Edges, rounded = false): string | null {
  if (edges.n === 0 && edges.e === 0 && edges.s === 0 && edges.w === 0) return null;
  const k = key(edges);
  if (rounded) {
    const r = ROUNDED_BY_EDGES.get(k);
    if (r) return r;
  }
  const exact = CHAR_BY_EDGES.get(k);
  if (exact) return exact;

  // Collapse to the most common non-zero weight and retry.
  const counts = new Map<Weight, number>();
  for (const v of [edges.n, edges.e, edges.s, edges.w]) {
    if (v !== 0) counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  let dominant: Weight = 1;
  let best = 0;
  for (const [weight, count] of counts) {
    if (count > best || (count === best && weight > dominant)) {
      best = count;
      dominant = weight;
    }
  }
  const collapsed: Edges = {
    n: edges.n === 0 ? 0 : dominant,
    e: edges.e === 0 ? 0 : dominant,
    s: edges.s === 0 ? 0 : dominant,
    w: edges.w === 0 ? 0 : dominant,
  };
  return CHAR_BY_EDGES.get(key(collapsed)) ?? CHAR_BY_EDGES.get(key({ n: edges.n ? 1 : 0, e: edges.e ? 1 : 0, s: edges.s ? 1 : 0, w: edges.w ? 1 : 0 } as Edges)) ?? null;
}

export function mergeEdges(a: Edges, b: Edges): Edges {
  return {
    n: (b.n || a.n) as Weight,
    e: (b.e || a.e) as Weight,
    s: (b.s || a.s) as Weight,
    w: (b.w || a.w) as Weight,
  };
}

export type Direction = 'n' | 'e' | 's' | 'w';

export const OPPOSITE: Record<Direction, Direction> = { n: 's', s: 'n', e: 'w', w: 'e' };
export const DELTA: Record<Direction, [number, number]> = {
  n: [0, -1],
  e: [1, 0],
  s: [0, 1],
  w: [-1, 0],
};

export interface BoxPlanCell {
  x: number;
  y: number;
  char: string;
}

/**
 * Accumulates the edges a drawing operation wants, merges them with what is
 * already on the canvas, then resolves every affected cell — including the ring
 * of untouched neighbours, so existing lines grow the right junction when a new
 * line meets them.
 */
export class BoxPlanner {
  private wanted = new Map<string, Edges>();
  private touched = new Set<string>();

  constructor(
    private readonly readChar: (x: number, y: number) => string,
    private readonly rounded = false,
  ) {}

  private static k(x: number, y: number): string {
    return `${x},${y}`;
  }

  /** Requests an edge on a cell (and, implicitly, its neighbour's mirror edge). */
  addEdge(x: number, y: number, dir: Direction, weight: Weight): void {
    if (weight === 0) return;
    const k = BoxPlanner.k(x, y);
    const current = this.wanted.get(k) ?? { ...NO_EDGES };
    current[dir] = weight;
    this.wanted.set(k, current);
    this.touched.add(k);
  }

  /** Horizontal run from (x1,y) to (x2,y) inclusive. */
  addHorizontal(x1: number, x2: number, y: number, weight: Weight): void {
    const [a, b] = x1 <= x2 ? [x1, x2] : [x2, x1];
    for (let x = a; x <= b; x++) {
      if (x > a) this.addEdge(x, y, 'w', weight);
      if (x < b) this.addEdge(x, y, 'e', weight);
      if (a === b) this.addEdge(x, y, 'e', weight);
    }
  }

  /** Vertical run from (x,y1) to (x,y2) inclusive. */
  addVertical(x: number, y1: number, y2: number, weight: Weight): void {
    const [a, b] = y1 <= y2 ? [y1, y2] : [y2, y1];
    for (let y = a; y <= b; y++) {
      if (y > a) this.addEdge(x, y, 'n', weight);
      if (y < b) this.addEdge(x, y, 's', weight);
      if (a === b) this.addEdge(x, y, 's', weight);
    }
  }

  addRect(x: number, y: number, width: number, height: number, weight: Weight): void {
    const x2 = x + width - 1;
    const y2 = y + height - 1;
    if (width <= 0 || height <= 0) return;
    if (height === 1) {
      this.addHorizontal(x, x2, y, weight);
      return;
    }
    if (width === 1) {
      this.addVertical(x, y, y2, weight);
      return;
    }
    this.addHorizontal(x, x2, y, weight);
    this.addHorizontal(x, x2, y2, weight);
    this.addVertical(x, y, y2, weight);
    this.addVertical(x2, y, y2, weight);
  }

  /** Resolves all touched cells plus their neighbours into concrete glyphs. */
  resolve(): BoxPlanCell[] {
    // Candidate set: every requested cell and each of its 4 neighbours, since a
    // new line arriving at an existing one changes that existing glyph too.
    const candidates = new Map<string, [number, number]>();
    for (const k of this.touched) {
      const [x, y] = k.split(',').map(Number);
      candidates.set(k, [x, y]);
      for (const dir of ['n', 'e', 's', 'w'] as Direction[]) {
        const [dx, dy] = DELTA[dir];
        candidates.set(BoxPlanner.k(x + dx, y + dy), [x + dx, y + dy]);
      }
    }

    // Effective edges = existing glyph's edges merged with requested ones.
    const effective = new Map<string, Edges>();
    for (const [k, [x, y]] of candidates) {
      const existing = edgesOf(this.readChar(x, y)) ?? { ...NO_EDGES };
      const want = this.wanted.get(k);
      effective.set(k, want ? mergeEdges(existing, want) : existing);
    }

    const edgesAt = (x: number, y: number): Edges =>
      effective.get(BoxPlanner.k(x, y)) ?? edgesOf(this.readChar(x, y)) ?? { ...NO_EDGES };

    const out: BoxPlanCell[] = [];
    for (const [k, [x, y]] of candidates) {
      const base = effective.get(k) as Edges;
      if (base.n === 0 && base.e === 0 && base.s === 0 && base.w === 0) continue;

      // Mutual connection: only link to a neighbour that points back at us.
      const linked: Edges = { ...base };
      for (const dir of ['n', 'e', 's', 'w'] as Direction[]) {
        const [dx, dy] = DELTA[dir];
        const neighbour = edgesAt(x + dx, y + dy);
        const back = neighbour[OPPOSITE[dir]];
        if (back !== 0 && linked[dir] === 0) linked[dir] = back;
      }

      const ch = charForEdges(linked, this.rounded);
      if (ch && ch !== this.readChar(x, y)) out.push({ x, y, char: ch });
    }
    return out;
  }
}

/** The four corner/side glyphs for a plain (non-connecting) rectangle. */
export interface BoxStyleSet {
  topLeft: string;
  top: string;
  topRight: string;
  left: string;
  right: string;
  bottomLeft: string;
  bottom: string;
  bottomRight: string;
  cross: string;
  teeDown: string;
  teeUp: string;
  teeRight: string;
  teeLeft: string;
}

export function styleSet(style: LineStyle): BoxStyleSet {
  const w = styleWeight(style);
  const rounded = style === 'rounded';
  const c = (edges: Edges, fallback: string) => charForEdges(edges, rounded) ?? fallback;
  return {
    topLeft: c({ n: 0, e: w, s: w, w: 0 }, '+'),
    top: c({ n: 0, e: w, s: 0, w }, '-'),
    topRight: c({ n: 0, e: 0, s: w, w }, '+'),
    left: c({ n: w, e: 0, s: w, w: 0 }, '|'),
    right: c({ n: w, e: 0, s: w, w: 0 }, '|'),
    bottomLeft: c({ n: w, e: w, s: 0, w: 0 }, '+'),
    bottom: c({ n: 0, e: w, s: 0, w }, '-'),
    bottomRight: c({ n: w, e: 0, s: 0, w }, '+'),
    cross: c({ n: w, e: w, s: w, w }, '+'),
    teeDown: c({ n: 0, e: w, s: w, w }, '+'),
    teeUp: c({ n: w, e: w, s: 0, w }, '+'),
    teeRight: c({ n: w, e: w, s: w, w: 0 }, '+'),
    teeLeft: c({ n: w, e: 0, s: w, w }, '+'),
  };
}
